import React, { useState } from "react";
import { withFormik, FormikProps, FormikErrors, Form } from "formik";
import { IntlShape } from "react-intl";

import { TextFieldComponent, ClickableComponent, ResourceTextComponent } from "@app/core";
import { validateEmail } from "@app/util";

import styles from "./login-component.module.scss";

export interface ILoginFormValues {
  emailAddress: string;
  password: string;
}

interface IOtherProps {
  intl: IntlShape;
  onNavigateToRequestPassword: (emailAddress: string) => void;
  isLoggingIn?: boolean;
}

const InnerForm = (props: IOtherProps & FormikProps<ILoginFormValues>) => {
  const { touched, errors, isLoggingIn } = props;
  const [emailAddress, setEmailAddress] = useState<string>("");

  const emailChanged = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmailAddress(e.target.value);
    props.handleChange(e);
  };

  return (
    <Form>
      <div className={styles.loginForm}>
        <div className={styles.formField}>
          <TextFieldComponent
            id="emailAddress"
            type="email"
            label={{
              label: props.intl.formatMessage({ id: "loginForm.email.label" }),
              errorMessage: touched.emailAddress === true && errors.emailAddress
            }}
            errorMessage={props.touched.emailAddress && props.errors.emailAddress}
            placeholder={props.intl.formatMessage({ id: "loginForm.email.placeholder" })}
            value={props.values.emailAddress}
            onChange={emailChanged}
            onBlur={props.handleBlur}
          />
        </div>
        <div className={styles.formField}>
          <div className={styles.forgotPassword}>
            <a
              role="link"
              onClick={() => {
                props.onNavigateToRequestPassword(emailAddress);
              }}
            >
              <ResourceTextComponent resourceKey="loginForm.forgotPassword" />
            </a>
          </div>
          <TextFieldComponent
            label={{
              label: props.intl.formatMessage({ id: "loginForm.password.label" }),
              errorMessage: touched.password === true && errors.password
            }}
            placeholder={props.intl.formatMessage({ id: "loginForm.email.placeholder" })}
            value={props.values.password}
            errorMessage={props.touched.password && props.errors.password}
            onChange={props.handleChange}
            onBlur={props.handleBlur}
            type="password"
            id="password"
          />
        </div>
      </div>
      <div className={styles.submitButton}>
        <ClickableComponent
          buttonType="submit"
          title={props.intl.formatMessage({ id: "loginForm.button.submit" })}
          fullWidth
          height={40}
          disabled={isLoggingIn}
        />
      </div>
    </Form>
  );
};

interface IFormProps {
  intl: IntlShape;
  onSubmit: (values: ILoginFormValues) => void;
}

export const LoginFormComponent = withFormik<IFormProps & IOtherProps, ILoginFormValues>({
  mapPropsToValues: () => ({
    emailAddress: "",
    password: ""
  }),

  validate: (values: ILoginFormValues, props) => {
    const errors: FormikErrors<ILoginFormValues> = {};
    if (!values.emailAddress) {
      errors.emailAddress = props.intl.formatMessage({ id: "loginForm.error.email.required" });
    } else if (!validateEmail(values.emailAddress)) {
      errors.emailAddress = props.intl.formatMessage({ id: "loginForm.error.email.invalid" });
    }
    if (!values.password) {
      errors.password = props.intl.formatMessage({ id: "loginForm.error.password.required" });
    }

    return errors;
  },

  handleSubmit: (values, bag) => {
    bag.props.onSubmit(values);
  }
})(InnerForm);
